import { Injectable } from '@angular/core';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  AuthenticationResult,
  AccountInfo,
  SilentRequest
} from '@azure/msal-browser';
import { ReplaySubject, Observable, throwError, TimeoutError } from 'rxjs';
import { map, mergeMap, catchError, tap, take, timeout } from 'rxjs/operators';
import { msalConfig } from './msalConfig';
import { ToastService } from '../toast/toast.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private msalInstance = new PublicClientApplication(msalConfig);

  private userName: ReplaySubject<string> = new ReplaySubject<string>(1);

  constructor(private readonly toast: ToastService) {
    this.initializeMsal();
  }

    public async initializeMsal(): Promise<void> {
      try {
        await this.msalInstance.initialize();
        const resp = await this.msalInstance.handleRedirectPromise();
        this.handleResponse(resp);
      } catch (error) {
        this.toast.ShowError(error?.message || error, false);
      }
  }

  private handleResponse(resp: AuthenticationResult) {
    let accountObj: AccountInfo;
    if (resp !== null) {
      accountObj = resp.account;
    } else {
      const currentAccounts = this.msalInstance.getAllAccounts();
      if (currentAccounts === null || currentAccounts.length === 0) {
        this.signIn();
      } else if (currentAccounts.length !== 1) {
        this.toast.ShowError('shared.auth.multipleAccounts');
        this.signOut()
      } else {
        const index = 0;
        accountObj = currentAccounts[index];
      }
    }
    if (accountObj != null) {
      this.userName.next(accountObj.username);
    }
  }

  public getUserName(): Observable<string> {
    return this.userName.asObservable();
  }

  public signOut() {
    this.msalInstance.logoutRedirect();
  }

  public signIn() {
    this.msalInstance.loginRedirect({
      scopes: ['openid', 'profile'], // v5: Always include OIDC scopes
    });
  }

  public getTokenRedirect(scopes: string[]): Observable<string> {
    let saverequest: SilentRequest = null;

    const observable = this.getUserName().pipe(
      take(1),
      timeout(3000),
      map((name) => {
        // v5: getAccountByUsername() removed, use getAllAccounts() and filter
        const accounts = this.msalInstance.getAllAccounts();
        const account = accounts.find(acc => acc.username === name) || accounts[0];
        const request: SilentRequest = {
          account: account,
          scopes
        };
        return request;
      }),
      tap((request) => {
        saverequest = request;
      }),
      mergeMap(async (request) => {
        return this.msalInstance.acquireTokenSilent(request);
      }),
      map((authResult) => {
        return authResult.accessToken;
      }),
      catchError((e) => {
        this.msalInstance
          .acquireTokenRedirect(saverequest)
          .catch((error) => this.toast.ShowError(error.message, false));

        return throwError(() => e);
      })
    );
    return observable;
  }
}
